import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import Window from "../components/Window";
import Icon, { IconTile } from "../components/Icon";
import { Avatar } from "../components/ProfileBits";
import { useBlocks } from "../blocks";
import { PenPal, getMyPenPals } from "../penpal";
import { usePins } from "../pins";
import PinButton from "../components/PinButton";
import { RemovePenPalButton } from "../components/SafetyBits";
import { doc, getDoc } from "firebase/firestore";
import "../styles.css";

// Everyone you're pen pals with, A to Z, with a quick way to write to them.
export default function AddressBook() {
  const [user] = useAuthState(auth);
  const { ids: blocked } = useBlocks();
  const [pals, setPals] = useState<PenPal[] | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!user) return;
    getMyPenPals(user.uid)
      .then(setPals)
      .catch((err) => {
        console.error(err);
        setPals([]);
      });
  }, [user]);

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const visible = (pals || [])
      .filter((p) => !blocked.has(p.id))
      .filter(
        (p) =>
          !term ||
          p.username.toLowerCase().includes(term) ||
          p.displayName.toLowerCase().includes(term)
      );
    const byLetter = new Map<string, PenPal[]>();
    visible.forEach((p) => {
      const first = (p.displayName || p.username).charAt(0).toUpperCase();
      const letter = /[A-Z]/.test(first) ? first : "#";
      byLetter.set(letter, [...(byLetter.get(letter) || []), p]);
    });
    return Array.from(byLetter.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [pals, blocked, search]);

  const total = (pals || []).filter((p) => !blocked.has(p.id)).length;

  return (
    <>
      <Window title="address_book.exe" tone="lavender">
        <div className="flex flex-wrap items-center gap-4">
          <IconTile name="book" tone="violet" size={52} />
          <div className="min-w-0 flex-1">
            <h1 className="window-heading">Address Book</h1>
            <p className="window-sub !mb-0">
              {pals === null
                ? "Flipping through the pages..."
                : total === 0
                ? "No pen pals yet. They'll show up here once a request is accepted."
                : `${total} pen pal${total === 1 ? "" : "s"}. Click someone to write to them.`}
            </p>
          </div>
          <Link to="/send" className="btn btn-sm">
            <Icon name="edit" size={14} /> Write a letter
          </Link>
        </div>
        {total > 3 && (
          <label className="field mt-4">
            <span className="field-label">Find a pen pal</span>
            <input
              className="input"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or @username"
            />
          </label>
        )}
      </Window>

      {pals !== null && total === 0 && (
        <div className="window">
          <p className="empty">
            <span className="empty-icon">
              <Icon name="heart" size={32} tone="pink" pop />
            </span>
            Find someone on the <Link to="/board">PenPal Board</Link> or in the{" "}
            <Link to="/board/directory">Directory</Link> and send them a pen pal
            request.
          </p>
        </div>
      )}

      {groups.length === 0 && total > 0 && (
        <p className="empty">Nobody matches "{search}".</p>
      )}

      {user && <SavedPosts uid={user.uid} />}

      {groups.map(([letter, people]) => (
        <section key={letter} className="address-section" aria-label={`Pen pals: ${letter}`}>
          <h2 className="address-tab">{letter}</h2>
          <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {people.map((p) => (
              <li key={p.id} className="address-card">
                <Link to={`/send?to=${p.id}`} className="address-card-main">
                  <Avatar
                    config={p.avatarConfig}
                    icon={p.avatar}
                    tone={p.avatarTone}
                    size={52}
                  />
                  <span className="min-w-0">
                    <span className="block font-display text-lg font-semibold">
                      {p.displayName || `@${p.username}`}
                    </span>
                    {p.displayName && (
                      <span className="block text-sm text-ink-soft">@{p.username}</span>
                    )}
                    {p.pronouns && <span className="block text-sm">{p.pronouns}</span>}
                  </span>
                </Link>
                <div className="address-card-actions">
                  <Link to={`/send?to=${p.id}`} className="btn btn-sm">
                    <Icon name="edit" size={14} /> Write a letter
                  </Link>
                  <Link to={`/profile/${p.id}`} className="btn btn-plain btn-sm">
                    Profile
                  </Link>
                  <RemovePenPalButton
                    otherUid={p.id}
                    username={p.username}
                    onRemoved={() => setPals((prev) => (prev || []).filter((x) => x.id !== p.id))}
                  />
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}

type SavedAd = { id: string; subject?: string; username?: string; anonymous?: boolean; color?: string; gone?: boolean };

// Board posts you've pinned, so favorites sit next to your people.
function SavedPosts({ uid }: { uid: string }) {
  const pins = usePins(uid);
  const [ads, setAds] = useState<SavedAd[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      pins.map(async (id): Promise<SavedAd> => {
        try {
          const snap = await getDoc(doc(db, "penpalAds", id));
          return snap.exists() ? { id, ...(snap.data() as Omit<SavedAd, "id">) } : { id, gone: true };
        } catch {
          return { id, gone: true };
        }
      })
    ).then((list) => !cancelled && setAds(list.reverse()));
    return () => {
      cancelled = true;
    };
  }, [pins]);

  return (
    <Window title={`saved_posts (${pins.length})`} tone="blue">
      {pins.length === 0 ? (
        <p className="text-sm text-ink-soft">
          Nothing saved yet. Tap the <Icon name="pin" size={14} /> pin on any{" "}
          <Link to="/board">board post</Link> to keep it here.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {ads.map((ad) => (
            <li key={ad.id} className="saved-post" style={{ background: ad.color || "#fff" }}>
              {ad.gone ? (
                <span className="min-w-0 flex-1 text-sm italic text-ink-soft">
                  This post was taken down.
                </span>
              ) : (
                <Link to={`/ad/${ad.id}`} className="min-w-0 flex-1 text-ink no-underline">
                  <span className="block font-display font-semibold">{ad.subject}</span>
                  <span className="block font-hand">
                    {ad.anonymous ? "— a secret admirer ✿" : `— @${ad.username}`}
                  </span>
                </Link>
              )}
              <PinButton uid={uid} adId={ad.id} pinned />
            </li>
          ))}
        </ul>
      )}
    </Window>
  );
}
